/** Abstracts pointer/touch events into camera-agnostic drag + zoom callbacks. */
export interface CameraInputHandler {
  onDragStart(): void;
  onDrag(dx: number, dz: number): void;
  onDragEnd(): void;
  onZoom(delta: number): void;
}

export class CameraInput {
  private _canvas: HTMLCanvasElement;
  private _handler: CameraInputHandler;
  private _lastX = 0;
  private _lastY = 0;
  private _lastPinchDist = 0;

  constructor(canvas: HTMLCanvasElement, handler: CameraInputHandler) {
    this._canvas = canvas;
    this._handler = handler;
    canvas.addEventListener('pointerdown', this._onPtrDown);
    canvas.addEventListener('pointermove', this._onPtrMove);
    canvas.addEventListener('pointerup', this._onPtrUp);
    canvas.addEventListener('pointercancel', this._onPtrUp);
    canvas.addEventListener('wheel', this._onWheel, { passive: false });
    canvas.addEventListener('touchstart', this._onTouchStart, { passive: false });
    canvas.addEventListener('touchmove', this._onTouchMove, { passive: false });
    canvas.addEventListener('touchend', this._onTouchEnd);
  }

  dispose(): void {
    const c = this._canvas;
    c.removeEventListener('pointerdown', this._onPtrDown);
    c.removeEventListener('pointermove', this._onPtrMove);
    c.removeEventListener('pointerup', this._onPtrUp);
    c.removeEventListener('pointercancel', this._onPtrUp);
    c.removeEventListener('wheel', this._onWheel);
    c.removeEventListener('touchstart', this._onTouchStart);
    c.removeEventListener('touchmove', this._onTouchMove);
    c.removeEventListener('touchend', this._onTouchEnd);
  }

  private _onPtrDown = (e: PointerEvent): void => {
    if (e.pointerType === 'touch') return;
    this._lastX = e.clientX; this._lastY = e.clientY;
    this._handler.onDragStart();
  };

  private _onPtrMove = (e: PointerEvent): void => {
    if (e.pointerType === 'touch') return;
    const dx = e.clientX - this._lastX, dy = e.clientY - this._lastY;
    this._lastX = e.clientX; this._lastY = e.clientY;
    this._handler.onDrag(dx, dy);
  };

  private _onPtrUp = (e: PointerEvent): void => {
    if (e.pointerType === 'touch') return;
    this._handler.onDragEnd();
  };

  private _onWheel = (e: WheelEvent): void => {
    e.preventDefault();
    this._handler.onZoom(e.deltaY);
  };

  private _onTouchStart = (e: TouchEvent): void => {
    e.preventDefault();
    if (e.touches.length === 1) {
      this._lastX = e.touches[0].clientX; this._lastY = e.touches[0].clientY;
      this._handler.onDragStart();
    } else if (e.touches.length === 2) {
      this._lastPinchDist = this._pinch(e.touches);
    }
  };

  private _onTouchMove = (e: TouchEvent): void => {
    e.preventDefault();
    if (e.touches.length === 1) {
      const dx = e.touches[0].clientX - this._lastX;
      const dy = e.touches[0].clientY - this._lastY;
      this._lastX = e.touches[0].clientX; this._lastY = e.touches[0].clientY;
      this._handler.onDrag(dx, dy);
    } else if (e.touches.length === 2) {
      const d = this._pinch(e.touches);
      this._handler.onZoom((this._lastPinchDist - d) * 12);
      this._lastPinchDist = d;
    }
  };

  private _onTouchEnd = (): void => { this._handler.onDragEnd(); };

  private _pinch(t: TouchList): number {
    const dx = t[0].clientX - t[1].clientX, dy = t[0].clientY - t[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }
}
